import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Send, FileText, Eye, X, CheckCircle, AlertTriangle, RotateCcw } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const STATUS_MAP = {
  gerada: { badge: 'badge-neutral', label: 'Gerada' },
  generated: { badge: 'badge-neutral', label: 'Gerada' },
  enviada: { badge: 'badge-warning', label: 'Enviada' },
  sent: { badge: 'badge-warning', label: 'Enviada' },
  protocolada: { badge: 'badge-success', label: 'Protocolada' },
};

export default function AdminLists() {
  const [listas, setListas] = useState([]);
  const [loading, setLoading] = useState(true);
  // Create list modal
  const [showCreate, setShowCreate] = useState(false);
  const [elegiveis, setElegiveis] = useState([]);
  const [selectedCases, setSelectedCases] = useState([]);
  const [listName, setListName] = useState('');
  const [reenviarModo, setReenviarModo] = useState(false);
  const [reenviarMotivo, setReenviarMotivo] = useState('');
  const [creating, setCreating] = useState(false);
  const [loadingElig, setLoadingElig] = useState(false);
  // Protocol modal
  const [protoModal, setProtoModal] = useState(null);
  const [protoForm, setProtoForm] = useState({ protocoloNumero: '', protocoloData: '', protocoloObs: '' });
  const [protoLoading, setProtoLoading] = useState(false);
  // Detail
  const [detailLista, setDetailLista] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchListas = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listarListas();
      setListas(res.data);
    } catch { toast.error('Erro ao carregar listas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchListas(); }, []);

  // Load eligible clients
  const openCreateModal = async () => {
    setShowCreate(true);
    setLoadingElig(true);
    setSelectedCases([]);
    setReenviarModo(false);
    setReenviarMotivo('');
    setListName(`Lista ${new Date().toLocaleDateString('pt-BR')}`);
    try {
      const res = await adminAPI.clientesElegiveis({ incluirJaListados: 'false' });
      setElegiveis(res.data);
    } catch { toast.error('Erro ao carregar elegiveis'); }
    finally { setLoadingElig(false); }
  };

  const toggleReenvio = async (val) => {
    setReenviarModo(val);
    setLoadingElig(true);
    try {
      const res = await adminAPI.clientesElegiveis({ incluirJaListados: val ? 'true' : 'false' });
      setElegiveis(res.data);
    } catch {}
    finally { setLoadingElig(false); }
  };

  const handleCreateList = async () => {
    if (!selectedCases.length) { toast.error('Selecione clientes'); return; }
    if (reenviarModo && !reenviarMotivo.trim()) { toast.error('Motivo do reenvio obrigatorio'); return; }
    setCreating(true);
    try {
      await adminAPI.criarLista({ caseIds: selectedCases, name: listName, reenviarModo, reenviarMotivo });
      toast.success('Lista criada!');
      setShowCreate(false);
      fetchListas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setCreating(false); }
  };

  const handleEnviar = async (id) => {
    try {
      await adminAPI.enviarLista(id);
      toast.success('Lista marcada como ENVIADA!');
      fetchListas();
      if (detailLista?.id === id) loadDetail(id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const handleProtocolar = async () => {
    if (!protoModal) return;
    setProtoLoading(true);
    try {
      const res = await adminAPI.protocolarLista(protoModal, { protocoloObs: protoForm.protocoloObs });
      toast.success(res.data.message || 'Lista PROTOCOLADA!');
      setProtoModal(null);
      setProtoForm({ protocoloObs: '' });
      fetchListas();
      if (detailLista?.id === protoModal) loadDetail(protoModal);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setProtoLoading(false); }
  };

  const loadDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await adminAPI.getLista(id);
      setDetailLista(res.data);
    } catch { toast.error('Erro'); }
    finally { setDetailLoading(false); }
  };

  const getStatus = (s) => STATUS_MAP[s] || STATUS_MAP.gerada;

  return (
    <div className="space-y-5" data-testid="admin-listas">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Listas</h1>
          <p className="text-sm text-white/50">{listas.length} lista(s)</p>
        </div>
        <button onClick={openCreateModal} className="btn-primary flex items-center gap-2 text-sm">
          <Plus size={16} /> Nova Lista
        </button>
      </div>

      {/* Lists table */}
      <div className="glass-card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10"><div className="rn-spinner" style={{width:24,height:24}} /></div>
        ) : listas.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={36} className="mx-auto text-white/15 mb-2" />
            <p className="text-sm text-white/35">Nenhuma lista criada.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.02]">
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-4">NOME</th>
                  <th className="text-center text-[10px] text-white/40 font-medium py-2.5 px-3">CLIENTES</th>
                  <th className="text-center text-[10px] text-white/40 font-medium py-2.5 px-3">STATUS</th>
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-3">PROTOCOLO</th>
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-3">CRIADA</th>
                  <th className="text-center text-[10px] text-white/40 font-medium py-2.5 px-3">ACOES</th>
                </tr>
              </thead>
              <tbody>
                {listas.map(l => {
                  const st = getStatus(l.status);
                  return (
                    <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="py-2.5 px-4">
                        <p className="text-xs font-medium text-white/90">{l.name}</p>
                        <p className="text-[9px] text-white/25 font-mono">{l.id?.slice(0,8)}</p>
                      </td>
                      <td className="py-2.5 px-3 text-center text-xs">{l.itemCount || l.caseIds?.length || 0}</td>
                      <td className="py-2.5 px-3 text-center"><span className={`${st.badge} text-[10px]`}>{st.label}</span></td>
                      <td className="py-2.5 px-3">
                        {l.protocoloNumero ? (
                          <div>
                            <p className="text-xs text-white/70">N: {l.protocoloNumero}</p>
                            <p className="text-[9px] text-white/30">{l.protocoloData}</p>
                          </div>
                        ) : <span className="text-[10px] text-white/25">—</span>}
                      </td>
                      <td className="py-2.5 px-3 text-xs text-white/35">{l.createdAt ? new Date(l.createdAt).toLocaleDateString('pt-BR') : '—'}</td>
                      <td className="py-2.5 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => loadDetail(l.id)} className="text-[9px] px-2 py-1 rounded-lg hover:bg-white/5 text-white/50">Ver</button>
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-lists/${l.id}/download-xlsx`} className="text-[9px] px-2 py-1 rounded-lg hover:bg-white/5" style={{color:'var(--rn-green)'}}>XLSX</a>
                          {l.status === 'gerada' && (
                            <button onClick={() => handleEnviar(l.id)} className="text-[9px] px-2 py-1 rounded-lg hover:bg-white/5" style={{color:'var(--rn-green)'}}>Enviar</button>
                          )}
                          {(l.status === 'enviada' || l.status === 'sent') && (
                            <button onClick={() => setProtoModal(l.id)} className="text-[9px] px-2 py-1 rounded-lg hover:bg-white/5" style={{color:'var(--rn-green)'}}>Protocolar</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Panel */}
      {detailLista && (
        <div className="glass-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-base font-semibold">{detailLista.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`${getStatus(detailLista.status).badge} text-[10px]`}>{getStatus(detailLista.status).label}</span>
                {detailLista.protocoloNumero && <span className="text-[10px] text-white/40">Protocolo: {detailLista.protocoloNumero}</span>}
              </div>
            </div>
            <button onClick={() => setDetailLista(null)} className="p-1.5 rounded-lg hover:bg-white/10"><X size={16} /></button>
          </div>
          {detailLoading ? (
            <div className="flex justify-center py-6"><div className="rn-spinner" style={{width:20,height:20}} /></div>
          ) : (
            <div className="space-y-2">
              {detailLista.items?.map(item => (
                <div key={item.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.02] border border-white/5">
                  <span className="text-xs text-white/80">{item.user?.name || 'N/A'}</span>
                  <span className="text-[10px] font-mono text-white/40">{item.case?.docFormatted}</span>
                  <span className="text-[10px] text-white/30 ml-auto">{item.case?.progress || 0}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* === CREATE LIST MODAL === */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-[#111817] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b border-white/10" style={{background:'rgba(17,24,23,0.95)',backdropFilter:'blur(12px)'}}>
              <h3 className="font-display text-lg font-semibold">Nova Lista</h3>
              <button onClick={() => setShowCreate(false)} className="p-1.5 rounded-lg hover:bg-white/10"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <input value={listName} onChange={e => setListName(e.target.value)} placeholder="Nome da lista"
                className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]" />

              {/* Toggle reenvio */}
              <div className="flex items-center gap-3 p-3 rounded-xl border border-white/10">
                <Checkbox checked={reenviarModo} onCheckedChange={toggleReenvio}
                  className="border-white/20 data-[state=checked]:bg-[var(--rn-green)] data-[state=checked]:border-[var(--rn-green)]" />
                <div>
                  <p className="text-sm text-white/80">Incluir clientes ja listados (REENVIO)</p>
                  <p className="text-[10px] text-white/35">Ativa modo reenvio — exige motivo obrigatorio</p>
                </div>
              </div>
              {reenviarModo && (
                <textarea value={reenviarMotivo} onChange={e => setReenviarMotivo(e.target.value)} placeholder="Motivo do reenvio (obrigatorio) *" rows={2}
                  className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]" />
              )}

              {/* Eligible clients */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-white/60">Clientes elegiveis ({elegiveis.length})</p>
                  <button onClick={() => setSelectedCases(selectedCases.length === elegiveis.length ? [] : elegiveis.map(e => e.caseId))}
                    className="text-[10px] hover:underline" style={{color:'var(--rn-green)'}}>
                    {selectedCases.length === elegiveis.length ? 'Desmarcar' : 'Selecionar todos'}
                  </button>
                </div>
                {loadingElig ? (
                  <div className="flex justify-center py-4"><div className="rn-spinner" style={{width:18,height:18}} /></div>
                ) : elegiveis.length === 0 ? (
                  <p className="text-xs text-white/30 text-center py-4">Nenhum cliente elegivel.</p>
                ) : (
                  <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                    {elegiveis.map(c => (
                      <label key={c.id} className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-colors ${
                        selectedCases.includes(c.caseId) ? 'bg-[rgba(99,203,182,0.06)] border border-[rgba(99,203,182,0.2)]' : 'bg-white/[0.02] border border-white/5 hover:border-white/10'
                      }`}>
                        <Checkbox checked={selectedCases.includes(c.caseId)} onCheckedChange={() => setSelectedCases(p => p.includes(c.caseId) ? p.filter(x=>x!==c.caseId) : [...p, c.caseId])}
                          className="border-white/20 data-[state=checked]:bg-[var(--rn-green)] data-[state=checked]:border-[var(--rn-green)]" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white/85">{c.name}</p>
                          <p className="text-[9px] text-white/40 font-mono">{c.cpf}</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {c.termAssinado && <span className="badge-success text-[8px]">Termo</span>}
                          {c.docEnviado && <span className="badge-success text-[8px]">Doc</span>}
                          {c.jaListado && <span className="badge-warning text-[8px]">Ja listado</span>}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <button onClick={handleCreateList} disabled={creating || !selectedCases.length}
                className="w-full btn-primary py-2.5 text-sm disabled:opacity-50">
                {creating ? <span className="rn-spinner mx-auto" style={{width:14,height:14}} /> : `Criar Lista (${selectedCases.length} clientes)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === PROTOCOLAR MODAL === */}
      {protoModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={() => setProtoModal(null)}>
          <div className="bg-[#111817] border border-white/10 rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold mb-2">Protocolar Lista</h3>
            <p className="text-xs text-white/40 mb-4">O numero do protocolo sera gerado automaticamente.</p>
            <div className="space-y-3">
              <textarea value={protoForm.protocoloObs} onChange={e => setProtoForm(p=>({...p,protocoloObs:e.target.value}))} placeholder="Observacao (opcional)" rows={2}
                className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]" />
              <div className="flex gap-3">
                <button onClick={() => setProtoModal(null)} className="btn-ghost flex-1 text-sm">Cancelar</button>
                <button onClick={handleProtocolar} disabled={protoLoading}
                  className="btn-primary flex-1 text-sm disabled:opacity-50">
                  {protoLoading ? <span className="rn-spinner mx-auto" style={{width:14,height:14}} /> : 'Protocolar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
